import * as Yup from 'yup'
import { useMemo, useRef, useState } from 'react'
import { FormikProps } from 'formik'
import { useSelector } from 'react-redux'

import { Child, MemberReturn, NUMBER_OF_CHILDREN, StepProps } from '../../../types'
import { Icons } from '../../../components/Icon'
import { actions, selectors } from '../../../redux'
import { ActionButtonLook, ActionButtonVariant } from '../../../components/ActionButton/styled'
import { Civility } from '../../../graphql/generated/graphql'

export const OTHER_FAMILY_RELATIONSHIP = 'other'

type AddMemberStepProps = StepProps & MemberReturn

export const useAddMemberStep = (props: AddMemberStepProps) => {
  const {
    t,
    dispatch,
    formValues,
    addMemberStepPartText,
    addMemberLabel,
    addMember,
    addAnotherChild,
    addMemberInitialValues,
    resetMember,
    typeOfAdd,
  } = props

  const formRef = useRef<FormikProps<any>>(undefined)
  const familyRelationships = useSelector(selectors.declaration.familyRelationships)
  const [isEditing, setIsEditing] = useState<boolean>(formValues.children.length === 0)

  const [hasMemberToDeclare, setHasMemberToDeclare] = useState<string>(
    addMemberInitialValues.hasMemberToDeclare ? '1' : '0'
  )

  const canAddAnotherChild = useMemo(
    () => addAnotherChild && Object.keys(addAnotherChild).length > 0,
    [addAnotherChild]
  )

  const displayFields = useMemo(
    () =>
      (formValues?.children?.length === 0 || isEditing || typeOfAdd === 'partner') &&
      hasMemberToDeclare === '1',
    [formValues?.children?.length, isEditing, typeOfAdd, hasMemberToDeclare]
  )

  const buildChildRelationshipPayload = (values: {
    familyRelationshipId?: string
    familyRelationshipCustom?: string
  }) => {
    if (typeOfAdd !== 'child') return {}

    const isOther = values.familyRelationshipId === OTHER_FAMILY_RELATIONSHIP

    return {
      familyRelationshipId: isOther ? null : values.familyRelationshipId || null,
      familyRelationshipCustom: isOther ? values.familyRelationshipCustom || null : null,
    }
  }

  return {
    stepPart: {
      text: addMemberStepPartText,
    },
    label: addMemberLabel,
    form: {
      innerRef: formRef,
      onContextUpdate: (context: any) => {
        setHasMemberToDeclare(context.values.hasMemberToDeclare)
      },
      initialValues: {
        hasMemberToDeclare: hasMemberToDeclare,
        civility: addMemberInitialValues.civility || '',
        lastName: addMemberInitialValues.lastName || '',
        firstName: addMemberInitialValues.firstName || '',
        familyRelationshipId: addMemberInitialValues.familyRelationshipId || '',
        familyRelationshipCustom: addMemberInitialValues.familyRelationshipCustom || '',
      },
      validationSchema: Yup.object().shape({
        hasMemberToDeclare: Yup.string().required(),
        civility: Yup.string().when('hasMemberToDeclare', {
          is: '1',
          then: (schema) => schema.required(),
        }),
        lastName: Yup.string().when('hasMemberToDeclare', {
          is: '1',
          then: (schema) => schema.required(),
        }),
        firstName: Yup.string().when('hasMemberToDeclare', {
          is: '1',
          then: (schema) => schema.required(),
        }),
        ...(typeOfAdd === 'child'
          ? {
              familyRelationshipId: Yup.string().when('hasMemberToDeclare', {
                is: '1',
                then: (schema) => schema.required(),
              }),
              familyRelationshipCustom: Yup.string().when(
                ['hasMemberToDeclare', 'familyRelationshipId'],
                {
                  is: (has: string, rel: string) =>
                    has === '1' && rel === OTHER_FAMILY_RELATIONSHIP,
                  then: (schema) => schema.required().max(255),
                }
              ),
            }
          : {}),
      }),
      onSubmit: (values: {
        hasMemberToDeclare: string
        civility: string
        lastName: string
        firstName: string
        familyRelationshipId?: string
        familyRelationshipCustom?: string
      }) => {
        if (hasMemberToDeclare === '1') {
          addMember({
            civility: values.civility,
            lastName: values.lastName,
            firstName: values.firstName,
            ...buildChildRelationshipPayload(values),
          })
        } else {
          resetMember()
        }

        if (!canAddAnotherChild && hasMemberToDeclare === '1') {
          dispatch(actions.declaration.setNextStep())
        } else if (!canAddAnotherChild && hasMemberToDeclare === '0') {
          dispatch(actions.declaration.setStep(8))
        }
      },
      validateOnChange: false,
      enableReinitialize: true,
    },
    fields: [
      {
        field: {
          displayFields,
          fields: {
            hasMemberToDeclare: {
              name: 'hasMemberToDeclare',
              allowMultiple: false,
              options: [
                {
                  label: t('declaration.form.addMember.hasMemberToDeclare.yes'),
                  value: '1',
                },
                {
                  label: t('declaration.form.addMember.hasMemberToDeclare.no'),
                  value: '0',
                },
              ],
            },
            civility: {
              label: t('declaration.form.addMember.civility.label'),
              name: 'civility',
              labelVariant: 'black',
              required: true,
              options: [
                {
                  label: '',
                  value: null,
                },
                {
                  label: t('declaration.form.addMember.civility.option.mrs'),
                  value: Civility.Mrs,
                },
                {
                  label: t('declaration.form.addMember.civility.option.mr'),
                  value: Civility.Mr,
                },
              ],
            },
            lastName: {
              name: 'lastName',
              label: t('declaration.form.addMember.lastName.label'),
              placeholder: t('declaration.form.addMember.lastName.placeholder'),
              labelVariant: 'black',
              required: true,
            },
            firstName: {
              name: 'firstName',
              label: t('declaration.form.addMember.firstName.label'),
              placeholder: t('declaration.form.addMember.firstName.placeholder'),
              labelVariant: 'black',
              required: true,
            },
            ...(typeOfAdd === 'child'
              ? {
                  familyRelationshipId: {
                    label: t('declaration.form.addMember.familyRelationship.label'),
                    name: 'familyRelationshipId',
                    labelVariant: 'black',
                    required: true,
                    options: [
                      { label: '', value: '' },
                      ...familyRelationships.map((fr) => ({
                        value: fr.id,
                        label: fr.name,
                      })),
                      {
                        label: t('declaration.form.addMember.familyRelationship.other'),
                        value: OTHER_FAMILY_RELATIONSHIP,
                      },
                    ],
                  },
                  familyRelationshipCustom: {
                    name: 'familyRelationshipCustom',
                    label: t('declaration.form.addMember.familyRelationshipCustom.label'),
                    placeholder: t(
                      'declaration.form.addMember.familyRelationshipCustom.placeholder'
                    ),
                    labelVariant: 'black',
                    required: true,
                  },
                }
              : {}),
          },
          ...(typeOfAdd === 'child'
            ? { description: t('declaration.form.addMember.familyMemberDescription') }
            : {}),
        },
        type: 'declarationAddMember',
      },
      ...(canAddAnotherChild && hasMemberToDeclare === '1'
        ? [
            {
              field: {
                shouldResetForm: false,
                addMemberButton: {
                  ...addAnotherChild,
                  text: isEditing
                    ? t('declaration.form.addMember.submit.add')
                    : t('declaration.form.addAnotherChild.text'),
                  ...(isEditing && {
                    icon: undefined,
                  }),
                  ...(canAddAnotherChild &&
                    !isEditing && {
                      onClick: () => {
                        setIsEditing(true)
                      },
                    }),
                  ...(canAddAnotherChild &&
                    isEditing && {
                      onClick: (values: any) => {
                        formRef.current?.setTouched(
                          {
                            civility: true,
                            firstName: true,
                            lastName: true,
                            ...(typeOfAdd === 'child' && {
                              familyRelationshipId: true,
                              familyRelationshipCustom: true,
                            }),
                          },
                          true
                        )

                        formRef.current?.validateForm().then((errors) => {
                          if (Object.keys(errors).length === 0) {
                            dispatch(
                              actions.declaration.setChild({
                                index: formValues.children.length,
                                child: {
                                  civility: values.civility,
                                  firstName: values.firstName,
                                  lastName: values.lastName,
                                  ...buildChildRelationshipPayload(values),
                                },
                              })
                            )

                            setIsEditing(false)

                            formRef.current?.resetForm()
                          }
                        })
                      },
                    }),
                  type: 'button',
                },
                members: formValues.children.map((child: Child, i: number) => ({
                  ...child,
                  civility: t(`civility.${child.civility.toLowerCase()}`),
                  button: {
                    iconProps: {
                      icon: Icons.trash,
                    },
                    variant: ActionButtonVariant.secondary,
                    look: ActionButtonLook.outlined,
                    onClick: () => {
                      dispatch(actions.declaration.removeChild(i))
                    },
                  },
                })),
              },
              type: 'declarationMemberList',
            },
          ]
        : []),
    ],
    button: {
      text: t('declaration.form.addMember.submit.label'),
      iconProps: {
        icon: Icons.arrowRight,
      },
      type: !canAddAnotherChild ? 'submit' : 'button',
      disabled: false,
      iconPosition: 'right',
      ...(canAddAnotherChild && {
        disabled: hasMemberToDeclare === '1' && formValues.children.length === 0,
        ...(hasMemberToDeclare === '0' && {
          onClick: () => {
            resetMember()
            dispatch(actions.declaration.setStep(8 + NUMBER_OF_CHILDREN + 1))
          },
        }),
        ...(hasMemberToDeclare === '1' &&
          formValues.children.length > 0 && {
            onClick: () => {
              dispatch(actions.declaration.setNextStep())
            },
          }),
      }),
    },
  }
}
